import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core';
import { VERTICAL_OPTIONS, type Vertical } from '../../../../core/enums/verticals';
import { TPipe } from '../../../../shared/pipes';
import { AppModalComponent } from '../../../../shared/ui/modal/app-modal.component';
import { SelectComponent } from '../../../../shared/ui/select/select.component';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { CompanyVerticalApiService } from '../../services/company-vertical-api.service';

@Component({
  selector: 'app-company-vertical-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, TPipe, AppModalComponent, SelectComponent],
  template: `
    <app-modal
      [visible]="visible()"
      [title]="i18n.t('companyVerticalModal.title')"
      [size]="'sm'"
      [closeOnOverlay]="!saving()"
      [closeOnEscape]="!saving()"
      [showCloseButton]="!saving()"
      (closed)="onClosed()"
    >
      <div modalBody class="flex flex-col gap-4 py-1">
        <p class="text-sm text-[var(--muted)]">
          {{ 'companyVerticalModal.description' | t }}
        </p>
        <app-select
          [label]="i18n.t('companyVerticalModal.fieldLabel')"
          [placeholder]="i18n.t('companyVerticalModal.fieldPlaceholder')"
          [options]="verticalOptions()"
          [value]="selectedVertical()"
          (valueChange)="onVerticalChange($event)"
        />
      </div>

      <div modalFooter>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-xl border border-[var(--border)] bg-transparent px-4 py-2 text-sm font-medium text-[var(--text)] transition hover:bg-[var(--hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="saving()"
          (click)="onLater()"
        >
          {{ 'companyVerticalModal.buttonLater' | t }}
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="!canConfirm()"
          (click)="onConfirm()"
        >
          @if (saving()) {
            <lucide-icon name="loader-circle" [size]="14" class="animate-spin" aria-hidden="true"></lucide-icon>
          }
          {{ 'companyVerticalModal.buttonConfirm' | t }}
        </button>
      </div>
    </app-modal>
  `,
})
export class CompanyVerticalModalComponent {
  protected readonly i18n = inject(I18nService);
  private readonly verticalApi = inject(CompanyVerticalApiService);
  private readonly toast = inject(ToastService);

  readonly visible = input<boolean>(false);
  readonly activeCompanyId = input<string | null>(null);
  readonly initialVertical = input<Vertical | null>(null);

  readonly closed = output<void>();
  readonly verticalSelected = output<Vertical>();

  protected readonly selectedVertical = signal<Vertical | null>(null);
  protected readonly saving = signal(false);

  protected readonly verticalOptions = computed(() =>
    VERTICAL_OPTIONS.map((option) => ({
      value: option.value,
      label: this.i18n.t(option.labelKey),
    }))
  );

  protected readonly canConfirm = computed(() => {
    const selected = this.selectedVertical();
    if (!selected || this.saving()) return false;
    if (this.initialVertical() === selected) return false;
    return true;
  });

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.selectedVertical.set(this.initialVertical());
      }
    });
  }

  protected onVerticalChange(value: string | string[] | null): void {
    if (Array.isArray(value)) return;
    if (!value) {
      this.selectedVertical.set(null);
      return;
    }
    const match = VERTICAL_OPTIONS.find((option) => option.value === value);
    this.selectedVertical.set(match?.value ?? null);
  }

  protected async onConfirm(): Promise<void> {
    const vertical = this.selectedVertical();
    if (!vertical || this.saving()) return;
    this.saving.set(true);
    try {
      await this.verticalApi.updateVertical(vertical, this.activeCompanyId());
      this.verticalSelected.emit(vertical);
      this.closed.emit();
    } catch {
      this.toast.show(this.i18n.t('companyVerticalModal.errorMessage'), 'error');
    } finally {
      this.saving.set(false);
    }
  }

  protected onLater(): void {
    if (this.saving()) return;
    this.closed.emit();
  }

  protected onClosed(): void {
    if (this.saving()) return;
    this.closed.emit();
  }
}
